import { useId, useState } from 'react';

export interface LinkFormValues {
  destination_url: string;
  slug?: string;
  title?: string;
  expires_at?: string;
  max_clicks?: number;
}

export default function LinkForm({
  initial,
  submitLabel = 'Create',
  onSubmit,
  onCancel,
  busy,
  error,
}: {
  initial?: Partial<LinkFormValues>;
  submitLabel?: string;
  onSubmit: (values: LinkFormValues) => void;
  onCancel?: () => void;
  busy?: boolean;
  error?: string | null;
}) {
  const [destinationUrl, setDestinationUrl] = useState(initial?.destination_url ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [expiresAt, setExpiresAt] = useState(initial?.expires_at?.slice(0, 16) ?? '');
  const [maxClicks, setMaxClicks] = useState<string>(
    initial?.max_clicks != null ? String(initial.max_clicks) : '',
  );

  // useId() lets multiple LinkForm instances coexist without colliding on
  // hard-coded ids. The htmlFor/id pairing also fixes screen-reader and
  // Playwright getByLabel access — both were broken before.
  const idDest = useId();
  const idSlug = useId();
  const idTitle = useId();
  const idExpires = useId();
  const idMaxClicks = useId();

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          destination_url: destinationUrl.trim(),
          slug: slug.trim() || undefined,
          title: title.trim() || undefined,
          expires_at: expiresAt ? new Date(expiresAt).toISOString() : undefined,
          max_clicks: maxClicks ? Number(maxClicks) : undefined,
        });
      }}
    >
      <div>
        <label className="label" htmlFor={idDest}>Destination URL</label>
        <input
          id={idDest}
          required
          type="url"
          className="input mt-1"
          placeholder="https://example.com/long/url"
          value={destinationUrl}
          onChange={(e) => setDestinationUrl(e.target.value)}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor={idSlug}>Custom slug (optional)</label>
          <input
            id={idSlug}
            type="text"
            className="input mt-1"
            placeholder="auto-generate if empty"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor={idTitle}>Title (optional)</label>
          <input
            id={idTitle}
            type="text"
            className="input mt-1"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor={idExpires}>Expires at (optional)</label>
          <input
            id={idExpires}
            type="datetime-local"
            className="input mt-1"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor={idMaxClicks}>Max clicks (optional)</label>
          <input
            id={idMaxClicks}
            type="number"
            min={1}
            className="input mt-1"
            value={maxClicks}
            onChange={(e) => setMaxClicks(e.target.value)}
          />
        </div>
      </div>
      {error && (
        <p className="rounded-md bg-red-50 p-2 text-sm text-red-700">{error}</p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
